import type { MouseEvent as ReactMouseEvent } from 'react'
import { Badge, Box, Group, ScrollArea, Stack, Text, Tooltip } from '@mantine/core'
import { IconExternalLink, IconPaperclip, IconPhoto } from '@/components/icons'
import { AppModal, InlineLoader } from '@/components/ui'
import { formatAttachmentFileSize } from '@/components/tickets/TicketMessageThread'
import { formatDateTimeDot } from '@/lib/ticket-display'
import { getAttachmentPath, resolveUploadsUrl } from '@/lib/uploads'
import type { CustomerAttachmentItem } from '@/types/api'

type TicketCustomerAttachmentsModalProps = {
  opened: boolean
  loading: boolean
  loaded: boolean
  attachments: CustomerAttachmentItem[]
  onClose: () => void
  onOpenTicket: (ticketId: string) => void
}

export function TicketCustomerAttachmentsModal({
  opened,
  loading,
  loaded,
  attachments,
  onClose,
  onOpenTicket,
}: TicketCustomerAttachmentsModalProps) {
  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={`Все вложения клиента${loaded ? ` (${attachments.length})` : ''}`}
      size="lg"
      centered
    >
      <ScrollArea h={520} type="auto" offsetScrollbars>
        {loading ? (
          <InlineLoader label="Загрузка вложений клиента…" />
        ) : attachments.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            У клиента пока нет вложений.
          </Text>
        ) : (
          <Stack gap={8}>
            {attachments.map((attachment) => (
              <CustomerAttachmentRow
                key={attachment.id}
                attachment={attachment}
                onOpenTicket={onOpenTicket}
              />
            ))}
          </Stack>
        )}
      </ScrollArea>
    </AppModal>
  )
}

function CustomerAttachmentRow({
  attachment,
  onOpenTicket,
}: {
  attachment: CustomerAttachmentItem
  onOpenTicket: (ticketId: string) => void
}) {
  const rawPath = getAttachmentPath(attachment)
  const href = rawPath ? resolveUploadsUrl(rawPath) : ''
  const name = attachment.original_filename || attachment.filename || 'файл'
  const ext = (name.includes('.') ? name.split('.').pop() : '') || ''
  const format = (ext || attachment.file_type || attachment.mime_type || '').toString().toUpperCase()
  const isImage =
    attachment.file_type === 'image' ||
    (attachment.mime_type || '').toLowerCase().startsWith('image/')

  const openFile = () => {
    if (href) window.open(href, '_blank', 'noopener,noreferrer')
  }

  const openTicket = (event: ReactMouseEvent) => {
    event.stopPropagation()
    onOpenTicket(attachment.ticket_id)
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      title={href ? 'Открыть вложение' : undefined}
      onClick={openFile}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          openFile()
        }
      }}
      p="xs"
      style={{
        borderRadius: 8,
        cursor: href ? 'pointer' : 'default',
        border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
      }}
    >
      <Group gap={10} wrap="nowrap" align="flex-start">
        <Box
          style={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: 6,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))',
          }}
        >
          {isImage && href ? (
            <img
              src={href}
              alt={name}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              onError={(event) => {
                event.currentTarget.style.display = 'none'
              }}
            />
          ) : isImage ? (
            <IconPhoto size={22} stroke={1.5} />
          ) : (
            <IconPaperclip size={20} stroke={1.5} />
          )}
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" align="center">
            <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
              {name}
            </Text>
            {href ? <IconExternalLink size={14} stroke={1.5} style={{ flexShrink: 0 }} /> : null}
          </Group>
          <Text size="xs" c="dimmed">
            {[
              format || null,
              formatAttachmentFileSize(attachment.file_size),
              attachment.created_at ? formatDateTimeDot(attachment.created_at) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
          {attachment.ticket_number != null || attachment.ticket_subject ? (
            <Group gap={6} wrap="nowrap" align="center" mt={4}>
              <Tooltip label="Открыть обращение">
                <Badge
                  size="xs"
                  variant="light"
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                  onClick={openTicket}
                >
                  Обращение #{attachment.ticket_number ?? '—'}
                </Badge>
              </Tooltip>
              {attachment.ticket_subject ? (
                <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
                  {attachment.ticket_subject}
                </Text>
              ) : null}
            </Group>
          ) : null}
        </Box>
      </Group>
    </Box>
  )
}
